import { BodyText, toast } from "@/components/ui";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { isActionAvailable } from "@/utils/available-actions";
import { useState } from "react";
import type { z } from "zod";
import { FollowUpEditor } from "./FollowUpEditor";
import { DURATION_UNITS, type editFollowUpSchema } from "./schemas";

import {
  CareplanAvailableAction,
  useCarePlanSummary,
} from "../network/useCarePlanSummary";
import { useDeleteFollowUp } from "../network/useDeleteFollowUp";
import type { FollowUpResponse } from "../network/useFollowUp";
import { useUpdateFollowUp } from "../network/useUpdateFollowUp";

interface FollowUpProps {
  carePlanId: string;
  childEncounterId: string;
  followup?: FollowUpResponse;
}

export function FollowUp({
  carePlanId,
  childEncounterId,
  followup,
}: FollowUpProps) {
  const [isEditing, setIsEditing] = useState(
    !followup?.followupMessage?.trim(),
  );

  const updateFollowUp = useUpdateFollowUp({
    childEncounterId,
    carePlanId,
  });

  const deleteFollowUp = useDeleteFollowUp({
    childEncounterId,
    carePlanId,
  });

  const unit = DURATION_UNITS.find(
    (u) => u.value === followup?.followupDurationUnit,
  );

  const { data: careplanSummary } = useCarePlanSummary(childEncounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canUpdateFollowUps = isActionAvailable(
    CareplanAvailableAction.UPDATE_FOLLOW_UP,
    careplanAvailableActions,
  );

  const onSubmit = async (values: z.infer<typeof editFollowUpSchema>) => {
    try {
      await updateFollowUp.mutateAsync({
        followupMessage: values.text,
        followupDurationValue: values.total,
        followupDurationUnit: values.unit,
      });
    } catch (error) {
      toast.error("Failed to update follow up.");
    }
  };

  const onDelete = async () => {
    try {
      await deleteFollowUp.mutateAsync();
    } catch (error) {
      toast.error("Failed to delete follow up.");
    }
  };

  if (isEditing) {
    return (
      <FollowUpEditor
        onSubmit={onSubmit}
        onCancel={() => setIsEditing(false)}
        isLoading={updateFollowUp.isPending}
        followup={followup}
      />
    );
  }

  return (
    <EditableRecordButton
      onClick={() => setIsEditing(true)}
      onDelete={canUpdateFollowUps ? onDelete : undefined}
      disabled={!canUpdateFollowUps}
      deletePending={deleteFollowUp.isPending}
      deleteTooltipText="Delete Follow Up"
    >
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <BodyText
          size="sm"
          bold
          data-testid="followup-duration"
          textColor={canUpdateFollowUps ? "primary" : "tertiary"}
        >
          {`${followup?.followupDurationValue} ${unit?.label}`}
        </BodyText>
        <BodyText
          size="sm"
          preserveLineBreaks
          textColor={canUpdateFollowUps ? "primary" : "tertiary"}
        >
          {followup?.followupMessage}
        </BodyText>
      </div>
    </EditableRecordButton>
  );
}
